import { Component, Output, EventEmitter, Input, Inject } from "@angular/core";
import { combineLatest, forkJoin, Observable, of } from "rxjs";
import { mergeMap } from "rxjs/operators";

import { ButtonActions } from "../../../../app.constants";
import { StepAction } from "../../entities/step-action-entity";
import { DocumentPaButton } from "../../entities/document-pa-button";
import { DocumentPersonneAvertie } from "../../entities/document-pa.entity";
import { SubjectRelay } from "../../../../shared/commands/subject-relay.class";
import { TraductionPaEntity } from "../../entities/traduction-pa.entity";
import { DocumentPaTraductionUpdateOrder } from '../../entities/document-pa-traduction-update-order.entity';
import { GenericUpdateTraductionStepOrder } from "../../entities/document-pa-generic-update-traduction-step-order.entity";

import { DocumentPaService } from "../../services/document-pa-service";
import { ParcService } from "../../../structure/parc/services/parc.service";

@Component({
	selector: "document-pa-consignes-securite",
	templateUrl: "document-pa-consignes-securite.html",
	styleUrls: ["document-pa-consignes-securite.css"],
})
export class DocumentPaConsignesSecurite {

	@Output() actionRequested = new EventEmitter<StepAction>();

	traductionsObligations: TraductionPaEntity[] = [];
	traductionsSituationAnormal!: TraductionPaEntity[];
	traductionsRisquesTrav!: TraductionPaEntity[];
	private parcCodes: string[] = [];
	currentPaInstance!: DocumentPersonneAvertie;

	actionButtons: DocumentPaButton[] = [
		new DocumentPaButton(ButtonActions.PREVIOUS, "Back", "btn btn-primary"),
		new DocumentPaButton(ButtonActions.CANCEL, "Liste des PA", "btn btn-danger"),
		new DocumentPaButton(ButtonActions.SAVE, "Sauvegarder maintenant", "btn btn-warning"),
		new DocumentPaButton(ButtonActions.NEXT, "Next", "btn btn-success"),
	];

	constructor(
		@Inject(DocumentPaService) private documentPaService: DocumentPaService,
		@Inject(ParcService) private parcService: ParcService
	) {
		this.currentPa$.subscribe(
			(currentPa) => {
				if (currentPa) {
					this.currentPaInstance = new DocumentPersonneAvertie();
					this.currentPaInstance.id = currentPa.id;
					this.currentPaInstance.etat = currentPa.etat;
					this.currentPaInstance.duplicatedId = currentPa.duplicatedId;
					this.currentPaInstance.lastCompletedStep = currentPa.lastCompletedStep;
					this.currentPaInstance.categorie = currentPa.categorie;
					if (currentPa.id) {
						this.documentPaService.getStepDefinitionData(currentPa.id).subscribe(
							(definitionStepData) => {
								if (definitionStepData.id) {
									this.currentPaInstance.commune = definitionStepData.commune;
									this.applyVariableReplacements();
								} 
							}
						);
					}
					this.currentPaInstance = currentPa;
	
					if (currentPa.parcIds?.length) {
						this.loadParcCodes(currentPa.parcIds);
					}

					if (currentPa.categorie) {
						let situationAnormal$ = this.documentPaService.getTraductions(
							currentPa.categorie,
							"CONSIGNES_SITUATION_ANORMALE"
						);
						let obligationsEe$ = this.documentPaService.getTraductions(
							currentPa.categorie,
							"CONSIGNES_SECURITE_OBLIGATIONS_EE"
						);
						let risquesTrav$ = this.documentPaService.getTraductions(
							currentPa.categorie,
							"CONSIGNES_SECURITE_RISQUES_TRAV_ISO"
						);
						let data$ = combineLatest(situationAnormal$, obligationsEe$, risquesTrav$).pipe(
							mergeMap(
								(data) => {
									return forkJoin({
										situationAnormal: of(data[0]),
										obligations: of(data[1]),
										risquesTrav: of(data[2])
									});
								}
							)
						);
						data$.subscribe((data) => {						
							this.traductionsSituationAnormal = data.situationAnormal as TraductionPaEntity[];
							this.traductionsObligations = data.obligations as TraductionPaEntity[];
							this.traductionsRisquesTrav = data.risquesTrav as TraductionPaEntity[];

							this.applyVariableReplacements();
						});
					}
				}
			}
		);
	}

	private currentPa$ = new SubjectRelay<DocumentPersonneAvertie>();
	@Input() set currentPa(obs: Observable<DocumentPersonneAvertie> | undefined) {
		this.currentPa$.registerSource(obs);
	}

	private replaceVariables(trad: TraductionPaEntity): TraductionPaEntity {
		if (!trad.fr) return trad;

		let text = trad.fr;

		if (this.currentPaInstance.commune) {
		    text = text.replace("$commune", this.currentPaInstance.commune);
		}


		if (this.parcCodes?.length) {
			text = text.replace("$parc", this.parcCodes.join(", "));
		}

		return { ...trad, fr: text };
	}

	loadParcCodes(ids: number[]): void {
		forkJoin(ids.map(id => this.parcService.getParcTLRById(id)))
			.subscribe(parcs => {
				this.parcCodes = parcs.filter(p => !!p).map(p => p.code);

				this.applyVariableReplacements();
			});
	}

	performAction(requestedAction: ButtonActions): void {
		console.log("Requested action:", requestedAction);

		switch (requestedAction) {

			case ButtonActions.CANCEL:
			case ButtonActions.RETURN_TO_LIST:
				this.actionRequested.emit(
					new StepAction(null, ButtonActions.RETURN_TO_LIST)
				);
				break;

			case ButtonActions.PREVIOUS:
				this.actionRequested.emit(
					new StepAction(this.currentPaInstance, ButtonActions.PREVIOUS)
				);
				break;

			case ButtonActions.SAVE:
			case ButtonActions.SAVE_DRAFT:
				this.saveConsignesSecurite(ButtonActions.RETURN_TO_LIST);
				break;

			case ButtonActions.NEXT:
				this.saveConsignesSecurite(ButtonActions.NEXT);
				break;

			default:
				break;
		}
	}

	private mapTraductionToUpdateOrder(trad: TraductionPaEntity): DocumentPaTraductionUpdateOrder {
		const upd = new DocumentPaTraductionUpdateOrder();
		upd.traductionId = trad.id;
		return upd;
	}


	private applyVariableReplacements(): void {
		
		if (this.traductionsSituationAnormal) {
			this.traductionsSituationAnormal = this.traductionsSituationAnormal.map(t => this.replaceVariables(t));
			
		}
		
		if (this.traductionsObligations) {
			this.traductionsObligations = this.traductionsObligations.map(t => this.replaceVariables(t));
		}

		if (this.traductionsRisquesTrav) {
			this.traductionsRisquesTrav = this.traductionsRisquesTrav.map(t => this.replaceVariables(t));
		}

	}
	
	private saveConsignesSecurite(afterSaveAction: ButtonActions): void {

		const allTraductions: TraductionPaEntity[] = [
			...this.traductionsSituationAnormal,
			...this.traductionsObligations,
			...this.traductionsRisquesTrav
		];

		const updateOrder = new GenericUpdateTraductionStepOrder();
		updateOrder.id = this.currentPaInstance?.id ?? 0;
		updateOrder.traductions = allTraductions.map(t =>
			this.mapTraductionToUpdateOrder(t)
		);

		this.documentPaService.updateSituationAnormaleStep(updateOrder).subscribe(
			(res) => {
				if (this.currentPaInstance) {
					this.currentPaInstance.id = res.id;
					if (this.currentPaInstance.lastCompletedStep < 6) {
						this.currentPaInstance.lastCompletedStep = 6;
					}
				}

				this.actionRequested.emit(
					new StepAction(this.currentPaInstance, afterSaveAction)
				);
			},
			(err) => console.error(
				"Erreurs lors de la sauvegarde des informations: ",
				err
			)
		);
	}

}
